import { AfterViewInit, Component, Input, NgZone, OnInit, OnDestroy, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FirestoreService } from '../../services/admin services/firestore.service';
import { interval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { UserService } from '../../services/user services/user.service';
import { StorageService } from '../../services/admin services/storage.service';
import { Timestamp } from 'firebase/firestore';
import { SessionManagementService } from '../../services/admin services/session-management.service';
import * as Hammer from 'hammerjs';
import { Share } from '@capacitor/share';
import { IDBPhotoCacheService } from '../../services/IDB/idbphoto-cache.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AnonymousUserService } from '../../services/user services/anonymous-user.service';





@Component({
  selector: 'app-photo-detail-modal',
  templateUrl: './photo-detail-modal.component.html',
  styleUrls: ['./photo-detail-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ]
})




export class PhotoDetailModalComponent implements OnInit, AfterViewInit, OnDestroy {


  expiresIn: number = 24 * 60 * 60 * 1000;
  photoId: string = '';
  photoUrl: string = '';
  deviceId: string = '';
  displayName: string = '';
  isPublisher: boolean = false;
  userId: string = '';
  timeRemaining: string = '';
  countdown: number = 0;
  likeStatus: 'none' | 'liked' | 'disliked' = 'none';
  isImageLoading: boolean = true;
  likeCount: number = 0;
  dislikeCount: number = 0;
  expirationDuration: number = 24 * 60 * 60 * 1000;
  photoTimestamp: Timestamp | null = null;
  priorityAdjustment: number = 0;
  timeAdjustment: number = 0;
  publisheruserId!: string;
  lowQualityUrl: string | null = null;
  highQualityUrl: string | null = null;
  isLowQualityLoaded: boolean = false;
  isHighQualityLoaded: boolean = false;
  hasInteractedOnce: boolean = false;
  private readonly storageKeyPrefix = 'photo_interaction_';

  // Zoom and pan properties
  private scale: number = 1;
  private posX: number = 0;
  private posY: number = 0;
  private hammerManager: any = null; // Fix type to avoid HammerManager not found error
  private lastScale: number = 1;

  // Subscription management
  private countdownSubscription: Subscription | null = null;
  private timerInterval: any = null;

  constructor(
    private _firestoreService: FirestoreService,
    private _anonymousUserService: AnonymousUserService,
    private _storageService: StorageService,
    private _sessionManagementService: SessionManagementService,
    private _ngZone: NgZone,
    private _IDBphotoCacheService: IDBPhotoCacheService,
    private _userService: UserService,
    public dialogRef: MatDialogRef<PhotoDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit() {
    console.log('🔍 PhotoDetail.ngOnInit() - Starting initialization');

    if (this.data) {
      console.log('📦 Input data received:', {
        photoId: this.data.photoId,
        displayName: this.data.displayName,
        isPublisher: this.data.isPublisher,
        userId: this.data.userId,
        expiresIn: this.data.expiresIn
      });

      this.photoId = this.data.photoId || '';
      this.photoUrl = this.data.photoUrl || '';
      this.displayName = this.data.displayName || '';
      this.isPublisher = this.data.isPublisher || false;
      this.userId = this.data.userId || '';
      this.expiresIn = this.data.expiresIn || (24 * 60 * 60 * 1000);

    } else {
      console.warn('⚠️ No input data provided to component');
    }

    // Get current user ID (fallback if not provided)
    if (!this.userId) {
      this.userId = this._anonymousUserService.currentUserId || '';
      console.log('👤 Using fallback userId from anonymousUserService:', this.userId);
    } else {
      console.log('👤 Using provided userId:', this.userId);
    }

    // Fetch initial photo data and start countdown
    console.log('🔄 Calling refreshPhotoData()');
    this.refreshPhotoData();

    console.log('🔐 Calling verifyPublisherStatus()');
    this.verifyPublisherStatus();

    console.log('👍 Calling initializeLikeStatus()');
    this.initializeLikeStatus();

    // Image starts as loading
    this.isImageLoading = true;
    console.log('⏳ Image loading state set to true');

    console.log('✨ ngOnInit() - Initialization complete');
  }

  ngAfterViewInit(): void {
    // Set up visibility change listener for zoom handler
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Initial attempt to initialize zoom handler
    setTimeout(() => {
      this.initializeZoomHandler();
    }, 500);
  }

  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      setTimeout(() => {
        this.initializeZoomHandler();
      }, 500);
    }
  }



  private async verifyPublisherStatus() {
    // console.log('[PhotoDetailModal] Verifying publisher status...');

    if (!this.photoId) {
      console.warn('[PhotoDetailModal] Missing photoId, cannot verify publisher status');
      return;
    }

    try {
      // Get photo data from Firestore
      const photoData = await this._firestoreService.getDocById(this.photoId);

      // Get device ID from IndexedDB as an additional check
      let uid = this._anonymousUserService.currentUserId;
      if (!uid) {
        console.log('[CameraService] No sync user ID available, fetching asynchronously');
        uid = this._anonymousUserService.currentUserId;
      }

      // Check all possible matches to determine if user is publisher
      const actualIsPublisher: boolean = !!(
        // Check against provided deviceId
        photoData.userId === uid ||
        photoData.uid === uid ||
        // Check against IndexedDB deviceId if available
        (uid && (photoData.userId === uid || photoData.deviceId === uid))
      );

      console.log('[PhotoDetailModal] Publisher check results:', {
        receivedIsPublisher: this.isPublisher,
        actualIsPublisher: actualIsPublisher,
        photoUserId: photoData.userId,
        photoDeviceId: photoData.deviceId,
        inputDeviceId: this.deviceId,
        uid: uid
      });

      // Update the isPublisher flag based on actual data
      if (this.isPublisher !== actualIsPublisher) {
        console.warn(
          '[PhotoDetailModal] Correcting isPublisher flag!',
          'Received:', this.isPublisher,
          'Actual:', actualIsPublisher
        );
        this.isPublisher = actualIsPublisher;
      }

    } catch (error) {
      console.error('[PhotoDetailModal] Error verifying publisher status:', error);
    }
  }

  addQualityParams(url: string, quality: 'low' | 'high'): string {
    // Parse the URL to separate base and query parameters
    const [baseUrl, queryString] = url.split('?');
    const params = new URLSearchParams(queryString || '');

    // Add quality-specific parameters
    if (quality === 'low') {
      params.set('maxsize', '300'); // Small size for quick loading
    } else {
      params.set('maxsize', '2000'); // High quality
    }

    // Add cache busting parameter
    params.set('cache', Date.now().toString());

    // Reconstruct the URL
    return `${baseUrl}?${params.toString()}`;
  }

  preloadHighQualityImage() {
    if (!this.highQualityUrl) return;

    // console.log('[PhotoDetailModal] Preloading high quality image:', this.highQualityUrl);

    const img = new Image();
    img.onload = () => {
      // console.log('[PhotoDetailModal] High quality image loaded successfully');
      this.isHighQualityLoaded = true;
    };
    img.onerror = (error) => {
      console.error('[PhotoDetailModal] Error loading high quality image:', error);
    };
    img.src = this.highQualityUrl;
  }

  onImageLoad(): void {
    //console.log('⭐ Image loaded event fired');

    // If this is the low quality image
    if (!this.isHighQualityLoaded && this.lowQualityUrl) {
      this.isLowQualityLoaded = true;
      this.isImageLoading = false;
    } else {
      // This is the high quality image or we're only loading one image
      this.isImageLoading = false;
    }

    // Initialize zoom handler after image is loaded
    setTimeout(() => {
      this.initializeZoomHandler();
    }, 100);
  }

  initializeZoomHandler(): void {
    // Clean up existing handler if it exists
    this.cleanupZoomHandler();

    const imageElement = document.querySelector('.full-photo') as HTMLImageElement;
    if (!imageElement) {
      console.warn('[PhotoDetailModal] Image element not found');
      return;
    }

    // console.log('[PhotoDetailModal] Found image element, initializing zoom handler');

    // First, set basic mouse events for desktop interactions
    imageElement.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });
    imageElement.addEventListener('mousedown', this.handleMouseDown.bind(this));
    document.addEventListener('mousemove', this.handleMouseMove.bind(this));
    document.addEventListener('mouseup', this.handleMouseUp.bind(this));
    imageElement.addEventListener('dblclick', this.handleDoubleClick.bind(this));

    // Set touch events for mobile interactions
    imageElement.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    imageElement.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    imageElement.addEventListener('touchend', this.handleTouchEnd.bind(this));
    imageElement.addEventListener('touchcancel', this.handleTouchEnd.bind(this));

    // Then try to set up Hammer.js as well
    try {
      this.hammerManager = new Hammer.Manager(imageElement);

      const pinch = new Hammer.Pinch();
      const pan = new Hammer.Pan({ direction: Hammer.DIRECTION_ALL });

      this.hammerManager.add([pinch, pan]);

      this.hammerManager.on('pinchstart', () => {
        this.lastScale = this.scale;
      });

      // Properly type the Hammer event
      this.hammerManager.on('pinch', (event: HammerInput) => {
        this._ngZone.run(() => {
          this.scale = Math.min(Math.max(0.5, this.lastScale * event.scale), 5);
          this.updateTransform(imageElement);
        });
      });

      // console.log('[PhotoDetailModal] Hammer.js zoom handler initialized successfully');
    } catch (error) {
      // If Hammer.js fails, we'll still have basic mouse/touch events
      console.warn('[PhotoDetailModal] Using fallback zoom handler:', error);
    }

    // Set initial cursor style
    imageElement.style.cursor = 'default';
  }

  // Mouse event handlers
  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    const imageElement = e.target as HTMLImageElement;
    const delta = Math.sign(-e.deltaY) * 0.1;
    this.scale = Math.min(Math.max(0.5, this.scale + delta), 5);
    this.updateTransform(imageElement);
  }

  private isDragging: boolean = false;
  private lastPosX: number = 0;
  private lastPosY: number = 0;

  private handleMouseDown(e: MouseEvent): void {
    const imageElement = e.target as HTMLImageElement;
    if (this.scale > 1) {
      this.isDragging = true;
      this.lastPosX = e.clientX;
      this.lastPosY = e.clientY;
      imageElement.style.cursor = 'grabbing';
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;

    const deltaX = e.clientX - this.lastPosX;
    const deltaY = e.clientY - this.lastPosY;
    this.posX += deltaX;
    this.posY += deltaY;
    this.lastPosX = e.clientX;
    this.lastPosY = e.clientY;

    const imageElement = document.querySelector('.full-photo') as HTMLImageElement;
    if (imageElement) {
      this.updateTransform(imageElement);
    }
  }

  private handleMouseUp(): void {
    if (!this.isDragging) return;

    this.isDragging = false;
    const imageElement = document.querySelector('.full-photo') as HTMLImageElement;
    if (imageElement) {
      imageElement.style.cursor = this.scale > 1 ? 'grab' : 'default';
    }
  }

  private handleDoubleClick(e: MouseEvent): void {
    const imageElement = e.target as HTMLImageElement;
    this.scale = 1;
    this.posX = 0;
    this.posY = 0;
    this.updateTransform(imageElement);
    imageElement.style.cursor = 'default';
  }

  // Touch event handlers
  private startDistance: number = 0;
  private startScale: number = 1;

  private handleTouchStart(e: TouchEvent): void {
    e.preventDefault();
    const imageElement = e.target as HTMLImageElement;

    if (e.touches.length === 2) {
      // Pinch zoom
      this.startDistance = this.getDistanceBetweenTouches(e);
      this.startScale = this.scale;
    } else if (e.touches.length === 1 && this.scale > 1) {
      // Single touch for dragging (only when zoomed)
      this.isDragging = true;
      this.lastPosX = e.touches[0].clientX;
      this.lastPosY = e.touches[0].clientY;
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    e.preventDefault();
    const imageElement = e.target as HTMLImageElement;

    if (e.touches.length === 2) {
      // Pinch zoom
      const currentDistance = this.getDistanceBetweenTouches(e);
      const newScale = (currentDistance / this.startDistance) * this.startScale;

      this._ngZone.run(() => {
        this.scale = Math.min(Math.max(0.5, newScale), 5);
        this.updateTransform(imageElement);
      });
    } else if (e.touches.length === 1 && this.isDragging) {
      // Dragging when zoomed
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;

      this._ngZone.run(() => {
        this.posX += currentX - this.lastPosX;
        this.posY += currentY - this.lastPosY;
        this.lastPosX = currentX;
        this.lastPosY = currentY;
        this.updateTransform(imageElement);
      });
    }
  }

  private getDistanceBetweenTouches(e: TouchEvent): number {
    if (e.touches.length < 2) return 0;

    const touch1 = e.touches[0];
    const touch2 = e.touches[1];
    return Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    );
  }

  private handleTouchEnd(e: TouchEvent): void {
    this.isDragging = false;
  }

  private cleanupZoomHandler(): void {
    // Clean up Hammer manager
    if (this.hammerManager) {
      try {
        this.hammerManager.destroy();
      } catch (error) {
        console.warn('[PhotoDetailModal] Error destroying Hammer manager:', error);
      }
      this.hammerManager = null;
    }

    // Clean up event listeners
    const imageElement = document.querySelector('.full-photo') as HTMLImageElement;
    if (imageElement) {
      imageElement.removeEventListener('wheel', this.handleWheel.bind(this));
      imageElement.removeEventListener('mousedown', this.handleMouseDown.bind(this));
      imageElement.removeEventListener('dblclick', this.handleDoubleClick.bind(this));
      imageElement.removeEventListener('touchstart', this.handleTouchStart.bind(this));
      imageElement.removeEventListener('touchmove', this.handleTouchMove.bind(this));
      imageElement.removeEventListener('touchend', this.handleTouchEnd.bind(this));
      imageElement.removeEventListener('touchcancel', this.handleTouchEnd.bind(this));
    }

    document.removeEventListener('mousemove', this.handleMouseMove.bind(this));
    document.removeEventListener('mouseup', this.handleMouseUp.bind(this));
  }

  private updateTransform(element: HTMLElement): void {
    element.style.transform = `translate(${this.posX}px, ${this.posY}px) scale(${this.scale})`;
  }

  onImageError() {
    this.isImageLoading = false;
    console.error('[PhotoDetailModal] Error loading image');
  }

  startCountdown() {
    // Clear any existing countdown first
    this.clearCountdown();

    this._firestoreService.getDocById(this.photoId).then((photoData: any) => {
      if (photoData && photoData.timestamp) {
        let timestamp;
        if (photoData.timestamp.toDate) {
          timestamp = photoData.timestamp.toDate();
        } else if (photoData.timestamp.seconds) {
          timestamp = new Date(photoData.timestamp.seconds * 1000);
        } else {
          timestamp = new Date(photoData.timestamp);
        }

        // Apply any time adjustments from the photo data
        const timeAdjustment = photoData.timeAdjustment || 0;
        const expirationTime = new Date(timestamp.getTime() + this.expiresIn + (timeAdjustment * 1000));
        const currentTime = new Date();
        const secondsRemaining = Math.max(0, Math.floor((expirationTime.getTime() - currentTime.getTime()) / 1000));

        this.countdown = secondsRemaining;
        this.formatTimeRemaining();

        this.countdownSubscription = interval(1000)
          .pipe(takeWhile(() => this.countdown > 0))
          .subscribe({
            next: () => {
              this.countdown--;
              this.formatTimeRemaining();
            },
            complete: () => {
              // Execute photo deletion when the countdown completes
              this.deleteExpiredPhoto();
            }
          });
      } else {
        // Fallback to default countdown
        this.countdown = this.expiresIn / 1000;
        this.formatTimeRemaining();

        this.countdownSubscription = interval(1000)
          .pipe(takeWhile(() => this.countdown > 0))
          .subscribe({
            next: () => {
              this.countdown--;
              this.formatTimeRemaining();
            },
            complete: () => {
              // Handle completion
              this.timeRemaining = 'Expired';
            }
          });
      }
    }).catch(error => {
      console.error('[PhotoDetailModal] Error getting photo timestamp:', error);
      // Fallback countdown
      this.countdown = this.expiresIn / 1000;
      this.formatTimeRemaining();
    });
  }

  private clearCountdown() {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = null;
    }

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private deleteExpiredPhoto() {
    if (this.countdown <= 0) {
      console.log('[PhotoDetailModal] Photo expired, deleting photo:', this.photoId);

      // First get the original photo document
      this._firestoreService.getDocById(this.photoId)
        .then(photoDoc => {
          // Save the original URL
          const originalUrl = photoDoc?.photoUrl; // Adjust field name if needed

          // Then delete from Firestore
          return this._firestoreService.deletePhotoFromFirestore(this.photoId)
            .then(() => {
              console.log('[PhotoDetailModal] Photo deleted from Firestore successfully');

              // Only attempt to delete from Storage if we have a valid URL
              if (originalUrl && originalUrl.includes('firebasestorage.googleapis.com')) {
                return this._storageService.deletePhotoFromStorage(originalUrl);
              } else {
                console.log('[PhotoDetailModal] No Firebase Storage URL found for deletion');
                return Promise.resolve(); // Continue the chain
              }
            });
        })
        .then(() => {
          console.log('[PhotoDetailModal] Photo deleted from Storage successfully');
          this.closeModal();
          this._sessionManagementService.performCleanup('reboot');
        })
        .catch(error => {
          console.error('[PhotoDetailModal] Error deleting expired photo:', error);
        });
    }
  }


  async deletePhoto() {
    if (confirm('Are you sure you want to delete this photo?')) {
      try {
        console.log('[PhotoDetailModal] Starting photo deletion process');

        const photoDoc = await this._firestoreService.getDocById(this.photoId);
        const originalUrl = photoDoc?.photoUrl;

        await this._firestoreService.deletePhotoFromFirestore(this.photoId);
        console.log('[PhotoDetailModal] Successfully deleted from Firestore');

        if (originalUrl && originalUrl.includes('firebasestorage.googleapis.com')) {
          await this._storageService.deletePhotoFromStorage(originalUrl);
          console.log('[PhotoDetailModal] Successfully deleted from Storage using original URL');
        }
        else if (this.photoUrl && this.photoUrl.includes('firebasestorage.googleapis.com')) {
          await this._storageService.deletePhotoFromStorage(this.photoUrl);
          console.log('[PhotoDetailModal] Successfully deleted from Storage using current URL');
        }
        else {
          console.log('[PhotoDetailModal] No Firebase Storage URL found for deletion');
        }

        try {
          if (this._IDBphotoCacheService) {
            await this._IDBphotoCacheService.removeUrl(this.photoId);
            console.log('[PhotoDetailModal] Removed URL from cache');
          }
        } catch (cacheError) {
          console.warn('[PhotoDetailModal] Error removing from cache:', cacheError);
        }

        await new Promise(resolve => setTimeout(resolve, 100));
        this.dialogRef.close({
          deleted: true,
          photoId: this.photoId
        });
        console.log('[PhotoDetailModal] Dialog closed successfully');

      } catch (error) {
        console.error('[PhotoDetailModal] Error in delete process:', error);
        try {
          this.dialogRef.close({
            deleted: true,
            photoId: this.photoId
          });
        } catch (closeError) {
          console.error('[PhotoDetailModal] Failed to close dialog:', closeError);
        }
      }
    }
  }

  async initializeLikeStatus() {
    try {
      // Get current user ID if not already set
      if (!this.userId) {
        this.userId = this._anonymousUserService.currentUserId || '';
      }

      // Fetch the photo data from Firestore
      const photoData = await this._firestoreService.getDocById(this.photoId);
      if (photoData) {
        const likes = photoData.likes || [];
        const dislikes = photoData.dislikes || [];

        // Set counts
        this.likeCount = likes.length;
        this.dislikeCount = dislikes.length;

        // Set user's status
        if (likes.includes(this.userId)) {
          this.likeStatus = 'liked';
          this.hasInteractedOnce = true;
        } else if (dislikes.includes(this.userId)) {
          this.likeStatus = 'disliked';
          this.hasInteractedOnce = true;
        } else {
          this.likeStatus = 'none';
          // Check local storage for previous interactions
          this.hasInteractedOnce = this.loadInteractionState();
        }

        // Save the current state to local storage if user has interacted
        if (this.hasInteractedOnce) {
          this.saveInteractionState();
        }
      }
    } catch (error) {
      console.error('[PhotoDetailModal] Error initializing like status:', error);
    }
  }

  async likePhoto() {
    try {
      // Skip if user has already interacted once
      if (this.hasInteractedOnce) return;

      // Existing like photo logic
      const previousStatus = this.likeStatus;
      const uid = this._anonymousUserService.currentUserId;
      if (!uid) {
        console.error('[PhotoDetailModal] User ID not available yet');
        return;
      }

      await this._firestoreService.likePhoto(this.photoId, uid);

      if (previousStatus === 'liked') {
        this.likeCount--;
        this.likeStatus = 'none';
      } else {
        this.likeCount++;
        if (previousStatus === 'disliked') {
          this.dislikeCount--;
        }
        this.likeStatus = 'liked';
      }

      // Set interaction flag to true and save to local storage
      this.hasInteractedOnce = true;
      this.saveInteractionState();

      await this.refreshPhotoData();
    } catch (error) {
      console.error('[PhotoDetailModal] Error liking photo:', error);
    }
  }

  async dislikePhoto() {
    try {
      // Skip if user has already interacted once
      if (this.hasInteractedOnce) return;

      // Existing dislike photo logic
      const previousStatus = this.likeStatus;
      const uid = this._anonymousUserService.currentUserId;
      if (!uid) {
        console.error('[PhotoDetailModal] User ID not available yet');
        return;
      }

      await this._firestoreService.dislikePhoto(this.photoId, uid);

      if (previousStatus === 'disliked') {
        this.dislikeCount--;
        this.likeStatus = 'none';
      } else {
        this.dislikeCount++;
        if (previousStatus === 'liked') {
          this.likeCount--;
        }
        this.likeStatus = 'disliked';
      }

      // Set interaction flag to true and save to local storage
      this.hasInteractedOnce = true;
      this.saveInteractionState();

      await this.refreshPhotoData();
    } catch (error) {
      console.error('[PhotoDetailModal] Error disliking photo:', error);
    }
  }

  async refreshPhotoData() {
    try {
      const photoData = await this._firestoreService.getDocById(this.photoId);

      if (photoData) {
        // Store photo timestamp and adjustments
        this.photoTimestamp = photoData.timestamp;
        this.timeAdjustment = photoData.timeAdjustment || 0;
        this.publisheruserId = photoData.userId;

        // Start or restart the countdown
        this.startCountdown();
      }
    } catch (error) {
      console.error('[PhotoDetailModal] Error refreshing photo data:', error);
    }
  }

  private loadInteractionState(): boolean {
    const storedState = localStorage.getItem(this.getStorageKey());
    if (storedState && ['liked', 'disliked', 'none'].includes(storedState)) {
      return true;
    }
    return false;
  }

  private saveInteractionState(): void {
    if (this.hasInteractedOnce) {
      localStorage.setItem(this.getStorageKey(), this.likeStatus);
    }
  }

  private getStorageKey(): string {
    const uid = this._anonymousUserService.currentUserId;
    return `${this.storageKeyPrefix}${this.photoId}_${uid}`;
  }

  formatTimeRemaining() {
    if (this.countdown <= 0) {
      this.timeRemaining = 'Expired';
      return;
    }

    const hours = Math.floor(this.countdown / 3600);
    const minutes = Math.floor((this.countdown % 3600) / 60);
    const seconds = this.countdown % 60;

    if (hours > 0) {
      this.timeRemaining = `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
    } else if (minutes > 0) {
      this.timeRemaining = `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    } else {
      this.timeRemaining = `${seconds}s`;
    }
  }

  async sharePhoto() {
    try {
      // Basic validation and setup
      if (!this.photoId) {
        this.showToast('Sorry, cannot share this photo.');
        return;
      }

      const shareTitle = 'A Photo from BlinkPic';
      let appDeepLink = `https://blinkpic-f7456.web.app/#/location`;

      // Get location data
      try {
        const photoData = await this._firestoreService.getDocById(this.photoId);

        if (photoData && photoData.location) {
          const latitude = photoData.location.latitude || photoData.location._lat;
          const longitude = photoData.location.longitude || photoData.location._long;

          // Just add the coordinates as query parameters
          appDeepLink += `?lat=${encodeURIComponent(latitude)}&lng=${encodeURIComponent(longitude)}`;
        }
      } catch (error) {
        console.warn('Could not retrieve location data');
      }

      // Create a meaningful description/text for the share with the link
      const shareText = `Check out this photo I captured with BlinkPic! ${appDeepLink}`;

      // Check if we're running in a native environment with Capacitor
      const isNativeEnvironment = typeof (window as any).Capacitor !== 'undefined' &&
        (window as any).Capacitor.isNativePlatform();

      // Try to add watermark if we're in a native environment
      let watermarkedPhotoUrl = this.photoUrl;
      if (isNativeEnvironment) {
        try {
          const watermarked = await this.addWatermark(this.photoUrl);
          if (watermarked) {
            watermarkedPhotoUrl = watermarked;
            console.log('Watermark added successfully');
          }
        } catch (watermarkError) {
          console.warn('Failed to add watermark, using original photo:', watermarkError);
          // Continue with original photo
        }
      }

      if (isNativeEnvironment) {
        // For native environments, use Capacitor's Share API
        try {
          await Share.share({
            title: shareTitle,
            text: shareText,
            url: watermarkedPhotoUrl,
            dialogTitle: 'Share Your Photo' // Only used on Android
          });
          console.log('Photo shared successfully');
        } catch (error) {
          console.error('Error sharing photo:', error);
          this.showToast('Unable to share the photo at this time.');
        }
      } else {
        // For PWA environments, we'll use the Web Share API if available
        if (navigator.share) {
          try {
            // Check if the browser supports file sharing
            const navigatorAny = navigator as any;
            const canShareFiles = navigatorAny.canShare &&
              navigatorAny.canShare({ files: [new File([], 'test.txt')] });

            let shareData: {
              title: string;
              text: string;
              url?: string;
              files?: File[];
            } = {
              title: shareTitle,
              text: shareText,
              url: appDeepLink  // Share the deep link
            };

            if (canShareFiles && watermarkedPhotoUrl.startsWith('data:')) {
              try {
                // Convert base64 to blob
                const response = await fetch(watermarkedPhotoUrl);
                const blob = await response.blob();
                // Create a File object from the blob
                const file = new File([blob], `photo_${this.photoId}.jpg`, { type: 'image/jpeg' });
                // Add the file to share data
                shareData.files = [file];
              } catch (conversionError) {
                console.warn('Could not convert image for sharing:', conversionError);
                // Continue with link-only sharing
              }
            }

            await navigator.share(shareData as ShareData);
            console.log('PWA: Photo shared successfully');
          } catch (error) {
            console.error('PWA: Error sharing photo:', error);
            this.showToast('Unable to share the photo. Your browser may not support this feature.');
          }
        } else {
          // Fallback for browsers that don't support Web Share API
          console.warn('Web Share API not supported in this browser');
          this.showToast('Sharing is not supported in this browser. Try using the download button instead.');
        }
      }
    } catch (error) {
      console.error('Unexpected error in sharePhoto:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }
      this.showToast('Unable to share the photo. Please try again later.');
    }
  }

  async downloadPhotoByThirdParty() {
    try {
      // Basic validation and setup
      if (!this.photoId || !this.photoUrl) {
        this.showToast('Sorry, cannot download this photo.');
        return;
      }

      // Check if we're running in a native environment with Capacitor
      const isNativeEnvironment = typeof (window as any).Capacitor !== 'undefined' &&
        (window as any).Capacitor.isNativePlatform();

      // For native environments, use Capacitor's Filesystem API
      if (isNativeEnvironment) {
        try {
          // Import necessary Capacitor plugins
          const { Filesystem, Directory } = (window as any).Capacitor.Plugins;

          // First, fetch the photo as a blob
          const response = await fetch(this.photoUrl);
          const blob = await response.blob();

          // Convert blob to base64
          const reader = new FileReader();

          // Handle the conversion as a promise
          const base64Data = await new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
              const base64 = reader.result as string;
              // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
              const base64Data = base64.split(',')[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          // Generate a filename with timestamp to prevent overwriting
          const timestamp = new Date().getTime();
          const fileName = `BlinkPic_${this.photoId}_${timestamp}.jpg`;

          // Save the file to device's download/pictures directory
          const result = await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.DOCUMENTS,
            recursive: true
          });

          console.log('File saved successfully:', result.uri);
          this.showToast('Photo downloaded successfully to your documents folder.');
        } catch (error) {
          console.error('Error downloading photo in native environment:', error);
          this.showToast('Unable to download the photo. Please check app permissions.');
        }
      } else {
        // For browsers, use traditional download approach
        try {
          // Create an anchor element
          const a = document.createElement('a');

          // Set the download attribute and filename
          a.download = `BlinkPic_${this.photoId}.jpg`;

          // If photoUrl is already a blob URL, use it directly
          if (this.photoUrl.startsWith('blob:')) {
            a.href = this.photoUrl;
          }
          // If it's a base64 data URL, use it directly
          else if (this.photoUrl.startsWith('data:')) {
            a.href = this.photoUrl;
          }
          // Otherwise, fetch it first
          else {
            // Fetch the image and create a blob URL
            const response = await fetch(this.photoUrl);
            const blob = await response.blob();
            a.href = URL.createObjectURL(blob);
          }

          // Programmatically click the anchor to trigger the download
          document.body.appendChild(a);
          a.click();

          // Clean up
          document.body.removeChild(a);

          // If we created a blob URL, revoke it to free memory
          if (!this.photoUrl.startsWith('blob:') && !this.photoUrl.startsWith('data:')) {
            URL.revokeObjectURL(a.href);
          }

          console.log('Photo downloaded successfully in browser');
          this.showToast('Photo downloaded successfully.');
        } catch (error) {
          console.error('Error downloading photo in browser:', error);
          this.showToast('Unable to download the photo. Please try again later.');
        }
      }
    } catch (error) {
      console.error('Unexpected error in downloadPhotoByThirdParty:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
      }
      this.showToast('Unable to download the photo. Please try again later.');
    }
  }

  // Keep the watermark function for native environment
  private async addWatermark(imageUrl: string): Promise<string> {
    console.log('[Watermark] Starting watermark process');

    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              reject(new Error('Unable to get canvas context'));
              return;
            }

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            // Add text watermark
            const watermarkText = 'BlinkPic';
            ctx.font = 'bold 24px Arial';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';

            // Position in bottom right
            const padding = 20;
            const textWidth = ctx.measureText(watermarkText).width;
            const x = canvas.width - textWidth - padding;
            const y = canvas.height - padding;

            ctx.fillText(watermarkText, x, y);
            ctx.strokeText(watermarkText, x, y);

            const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
            resolve(dataUrl);
          } catch (canvasError) {
            console.error('[Watermark] Canvas error:', canvasError);
            reject(canvasError);
          }
        };

        img.onerror = () => {
          reject(new Error('Failed to load image for watermarking'));
        };

        img.src = imageUrl;
      } catch (error) {
        reject(error);
      }
    });
  }


  private showToast(message: string): void {
    // Implement your toast logic here, or use the same method you use elsewhere
    // For example:
    // this.toastService.show(message);
  }

  formatDate(dateStr: string): string {
    if (!dateStr) {
      return 'N/A';
    }

    try {
      let date: Date;

      // Check if the date is in DD.MM.YYYY format
      if (dateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        const [day, month, year] = dateStr.split('.');
        date = new Date(`${year}-${month}-${day}`);
      } else {
        // Try standard date parsing
        date = new Date(dateStr);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('RSSService.formatDate: Invalid date:', dateStr);
        return dateStr;
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      console.error('RSSService.formatDate: Error formatting date:', e, dateStr);
      return dateStr;
    }
  }

  closeModal() {
    this.dialogRef.close({
      deleted: false,
      liked: this.likeStatus === 'liked' ? true :
        this.likeStatus === 'disliked' ? false : undefined
    });
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.clearCountdown();

    // Remove event listeners
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Clean up zoom handlers
    this.cleanupZoomHandler();
  }
}